import React, { useState, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spinner } from '@carvana/showroom-spinner';
import { fetchLoanDetails } from '../../../../ducks/actions';
import {
  getMyOnetimePaymentSchedules,
  getAutoPaySchedule,
  getCurrentVehicleId,
  getLoanDetailsInProgress,
} from '../../../../ducks/selectors';
import CancelPaymentDialog from '../../../future-packages/cancel-payment-dialog';
import { cancelPayment } from '../../../../services/MyCarsService';
import {
  ScheduledPaymentsInfo,
  ScheduledPaymentsWrapper,
  Title,
  SpinnerWrapper,
  NoScheduledPaymentsNote,
  HR,
} from './ScheduledPayments.styles';
import Payment from './payment';
import { sortPaymentWithdrawSchedules } from '../../../../utils/dateUtils';
import { formatCurrency } from '../../../../utils/CurrencyUtils';
import BridegcrestCustomError from '../../../future-packages/bridgecrest-custom-error';
import OopsError from '../../../future-packages/oops-error';
import { useDualEventTracking } from '../../../../utils/useDualEventTracking';

const ScheduledPayments = (props: { vehicleId: any }) => {
  /** Capture scroll position
   * For scrolling back to top after loading scheduled payments
   */
  const [scrollPosition, setPosition] = useState(0);
  useLayoutEffect(() => {
    function updatePosition() {
      setPosition(window.pageYOffset);
    }
    window.addEventListener('scroll', updatePosition);
    updatePosition();
    return () => window.removeEventListener('scroll', updatePosition);
  }, []);
  // Capture scroll position - End

  const dispatch = useDispatch();
  const { vehicleId } = props;
  const environment = process.env.NODE_ENV;
  const { trackEvent } = useDualEventTracking();
  const isLoanDetailsInprogress = useSelector(getLoanDetailsInProgress);

  // Payments
  const [paymentToCancel, setPaymentToCancel]: any = useState({});
  const oneTimePaymentSchedules = useSelector(getMyOnetimePaymentSchedules);
  const autoPaySchedule = useSelector(getAutoPaySchedule);
  const currentVehicleId = useSelector(getCurrentVehicleId);
  const paymentSchedules = [
    ...oneTimePaymentSchedules,
    ...autoPaySchedule,
  ].sort(sortPaymentWithdrawSchedules);

  /* Cancel Payment Modal state/methods */
  const [openCancelPaymentDialog, setOpenCancelPaymentDialog] = useState(false);

  /* Brigecrest Error Modal state/methods */
  const [isBridgecrestError, setBridgecrestError] = useState(false);
  const [bridgecrestErrorMessage, setBridgecrestErrorMessage] = useState('');

  /* Oops error modal state/methods */
  const [showOopsError, setOopsError] = useState(false);

  // Lang
  const title = 'Scheduled Payments';
  const scheduledPaymentsNote =
    'Payments scheduled using other payment options may not be listed.';
  const noScheduledPaymentsNote =
    'You have not scheduled any payments to your Bridgecrest account through the Carvana website. Payments scheduled using other payment options may not be listed.';

  // Show/ Hide Cancel Payment Modal
  const toggleCancelPaymentDialog = (selectedPayment?: any) => {
    if (selectedPayment) {
      setPaymentToCancel(selectedPayment);
    }
    setOpenCancelPaymentDialog(!openCancelPaymentDialog);

    // Event to track clicking the link and
    if (!openCancelPaymentDialog) {
      trackEvent('schedule_payments_cancel_dialogue');
    } else {
      trackEvent('schedule_payments_cancel_dialogue_exit_tap');
    }
  };

  // Cancel Payment Method to run on clicking cancel button in dialog box
  const [isCancelPaymentLoading, setIsCancelPaymentLoading] = useState(false);
  const cancelPaymentMethod = () => {
    /*
     * API Call to cancel the given payment - close dialog after
     */
    setIsCancelPaymentLoading(true);

    const { paymentId } = paymentToCancel;

    // API Payload
    const payload = {
      paymentId,
    };

    return cancelPayment(environment, currentVehicleId, payload)
      .then(() => {
        // Refresh the loan details
        dispatch(fetchLoanDetails(process.env.NODE_ENV, vehicleId));
        // Scroll to top
        window.scroll({
          top: scrollPosition,
          behavior: 'smooth',
        });
      })
      .catch((error: any) => {
        trackEvent('unable_to_cancel_payment_dialogue');
        if (
          error.response?.data?.errorCode === 'BridgecrestGraphQLErrorException'
        ) {
          setBridgecrestError(true);
          setBridgecrestErrorMessage(error.response.data.message);
        } else {
          setOopsError(true);
        }
        console.error(error);
      })
      .finally(() => {
        // Cancel Payment Event
        trackEvent('cancel_payment', {
          vehicleId,
          amount: formatCurrency(paymentToCancel.withdrawAmount),
          startDate: paymentToCancel.paymentDate,
        });
        setOpenCancelPaymentDialog(!openCancelPaymentDialog);
        setIsCancelPaymentLoading(false);
      });
  };

  const payments = paymentSchedules.map((paymentSchedule: any) => (
    <>
      <Payment
        key={paymentSchedule.paymentId}
        isRecurring={paymentSchedule.isRecurring}
        paymentSchedule={paymentSchedule}
        onCancelPayment={() => toggleCancelPaymentDialog(paymentSchedule)}
        onTurnOffAutopay={() =>
          trackEvent('auto_pay_turn_off_auto_pay_button_tap')
        }
        turnOffAutopayUrl={`/loan-detail/turn-off-autopay/${vehicleId}`}
      />
      <HR />
    </>
  ));

  const closeBridgecrestModal = () => {
    trackEvent('unable_to_cancel_payment_dialogue_exit_tap');
    setBridgecrestError(false);
    setBridgecrestErrorMessage('');
  };

  return isLoanDetailsInprogress ? (
    // Show loading icon if loan details are still being fetched.
    <SpinnerWrapper>
      <Spinner />
    </SpinnerWrapper>
  ) : (
    <>
      <ScheduledPaymentsWrapper>
        <Title>{title.toUpperCase()}</Title>
        {paymentSchedules.length ? (
          <ScheduledPaymentsInfo>{scheduledPaymentsNote}</ScheduledPaymentsInfo>
        ) : (
          <NoScheduledPaymentsNote>
            {noScheduledPaymentsNote}
          </NoScheduledPaymentsNote>
        )}
        {payments}
      </ScheduledPaymentsWrapper>
      {/* Cancel Payment Dialog */}
      <CancelPaymentDialog
        toggleModal={toggleCancelPaymentDialog}
        isCancelPaymentLoading={isCancelPaymentLoading}
        openModal={openCancelPaymentDialog}
        cancelPaymentMethod={cancelPaymentMethod}
        withdrawAmount={formatCurrency(paymentToCancel.withdrawAmount)}
      />
      {/* Cancel Payment Failure Dialog */}
      <BridegcrestCustomError
        isOpen={isBridgecrestError}
        header="CANCEL PAYMENT"
        title="UNABLE TO CANCEL PAYMENT"
        message={bridgecrestErrorMessage}
        closeModal={closeBridgecrestModal}
      />
      {/* Oops Error Dialog */}
      <OopsError
        isOpen={showOopsError}
        closeModal={() => setOopsError(false)}
      />
    </>
  );
};

export default ScheduledPayments;
